import React, { useState, useEffect, useMemo } from "react";
import {
  Plus,
  Search,
  Edit,
  AlertTriangle,
  FileText,
  CheckCircle,
} from "lucide-react";
import { toast } from "react-toastify";
import { SpinLoader } from "../../../components/loader/Loader";
import Modal from "../../../components/modal/Modal";
import { Dropdown } from "../../../components/dropdown/Dropdown";
import {
  getAllTheftLossReports,
  createTheftLossReport,
  updateTheftLossReport,
  markAssetRecovered,
  getTheftLossReportById,
} from "../../../service/securityService";
import {
  TheftLossReport,
  SecuritySearchFilters,
  PaginationParams,
} from "../../../types/security_types";
import axiosInstance from "../../../common/axiosInstance";
import { ColDef, themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

interface TheftLossReportFormData {
  assetId: string;
  assetName: string;
  assetTag?: string;
  reportType: "Theft" | "Loss" | "Missing" | "Damaged";
  location: string;
  zone?: string;
  description: string;
  estimatedValue?: number;
  policeReportNumber?: string;
  policeReportDate?: string;
  insuranceClaimNumber?: string;
  insuranceClaimDate?: string;
}

const TheftLossReporting: React.FC = () => {
  const [reports, setReports] = useState<TheftLossReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRegion, setSelectedRegion] = useState<string>("ALL Regions");
  const [selectedPremises, setSelectedPremises] =
    useState<string>("ALL Premises");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isRecoverModalOpen, setIsRecoverModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<TheftLossReport | null>(
    null
  );
  const [zones, setZones] = useState<Array<{ value: string; label: string }>>(
    []
  );
  const [assets, setAssets] = useState<Array<{ value: string; label: string }>>(
    []
  );
  const [pagination, setPagination] = useState<PaginationParams>({
    page: 1,
    limit: 20,
  });

  const [formData, setFormData] = useState<TheftLossReportFormData>({
    assetId: "",
    assetName: "",
    assetTag: "",
    reportType: "Theft",
    location: "",
    zone: "",
    description: "",
    estimatedValue: undefined,
    policeReportNumber: "",
    policeReportDate: "",
    insuranceClaimNumber: "",
    insuranceClaimDate: "",
  });

  const [recoveryData, setRecoveryData] = useState({
    recoveryLocation: "",
    recoveryNotes: "",
  });

  useEffect(() => {
    fetchReports();
    fetchZones();
    fetchAssets();
  }, [searchQuery, selectedRegion, selectedPremises, statusFilter, pagination]);

  const fetchZones = async () => {
    try {
      const response = await axiosInstance.get("/api/security/access-zones");
      if (response.data.success) {
        const zonesData = response.data.data || response.data.message || [];
        setZones(
          zonesData.map((zone: any) => ({
            value: zone._id || zone.id,
            label: zone.zoneName,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching zones:", error);
    }
  };

  const fetchAssets = async () => {
    try {
      // Fetch equipment from Equipment Management module
      const response = await axiosInstance.get("/api/security/assets/list", {
        params: { limit: 1000 },
      });
      const assetsData = response.data.data || [];
      setAssets(
        assetsData.map((asset: any) => ({
          value: asset._id || asset.id,
          label:
            asset.name ||
            asset.equipmentName ||
            asset.equipmentId ||
            "Unknown Equipment",
          serialNumber: asset.serialNumber,
          equipmentId: asset.equipmentId,
        }))
      );
    } catch (error) {
      console.error("Error fetching equipment:", error);
    }
  };

  const fetchReports = async () => {
    try {
      setLoading(true);
      const filters: SecuritySearchFilters = {};
      if (selectedRegion !== "ALL Regions") filters.region = selectedRegion;
      if (selectedPremises !== "ALL Premises")
        filters.premise = selectedPremises;
      if (searchQuery.trim()) filters.searchQuery = searchQuery;
      if (statusFilter !== "ALL") filters.status = statusFilter;

      const response = await getAllTheftLossReports(filters, pagination);
      setReports(response.data || []);
    } catch (error: any) {
      console.error("Error fetching theft/loss reports:", error);
      toast.error(error.response?.data?.resMessage || "Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setFormData({
      assetId: "",
      assetName: "",
      assetTag: "",
      reportType: "Theft",
      location: "",
      zone: "",
      description: "",
      estimatedValue: undefined,
      policeReportNumber: "",
      policeReportDate: "",
      insuranceClaimNumber: "",
      insuranceClaimDate: "",
    });
    setIsCreateModalOpen(true);
  };

  const handleEdit = (report: TheftLossReport) => {
    setSelectedReport(report);
    setFormData({
      assetId: report.assetId,
      assetName: report.assetName,
      assetTag: report.assetTag || "",
      reportType: report.reportType,
      location: report.location,
      zone: report.zone || "",
      description: report.description,
      estimatedValue: report.estimatedValue,
      policeReportNumber: report.policeReportNumber || "",
      policeReportDate: report.policeReportDate
        ? new Date(report.policeReportDate).toISOString().slice(0, 16)
        : "",
      insuranceClaimNumber: report.insuranceClaimNumber || "",
      insuranceClaimDate: report.insuranceClaimDate
        ? new Date(report.insuranceClaimDate).toISOString().slice(0, 16)
        : "",
    });
    setIsEditModalOpen(true);
  };

  const handleRecover = (report: TheftLossReport) => {
    setSelectedReport(report);
    setRecoveryData({ recoveryLocation: "", recoveryNotes: "" });
    setIsRecoverModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        policeReportDate: formData.policeReportDate
          ? new Date(formData.policeReportDate).toISOString()
          : undefined,
        insuranceClaimDate: formData.insuranceClaimDate
          ? new Date(formData.insuranceClaimDate).toISOString()
          : undefined,
      };

      if (isEditModalOpen && selectedReport) {
        const id = selectedReport._id || selectedReport.id;
        if (!id) return;
        await updateTheftLossReport(id, submitData);
        toast.success("Report updated successfully");
      } else {
        await createTheftLossReport(submitData);
        toast.success("Theft/Loss report created successfully");
      }
      setIsCreateModalOpen(false);
      setIsEditModalOpen(false);
      setSelectedReport(null);
      fetchReports();
    } catch (error: any) {
      console.error("Error saving report:", error);
      toast.error(error.response?.data?.resMessage || "Failed to save report");
    }
  };

  const handleRecoverSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedReport) return;

    try {
      const id = selectedReport._id || selectedReport.id;
      if (!id) return;
      await markAssetRecovered(
        id,
        recoveryData.recoveryLocation,
        recoveryData.recoveryNotes
      );
      toast.success("Asset marked as recovered");
      setIsRecoverModalOpen(false);
      setSelectedReport(null);
      fetchReports();
    } catch (error: any) {
      console.error("Error marking asset as recovered:", error);
      toast.error(
        error.response?.data?.resMessage || "Failed to mark asset as recovered"
      );
    }
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      Reported: "bg-blue-100 text-blue-800",
      "Under Investigation": "bg-yellow-100 text-yellow-800",
      Recovered: "bg-green-100 text-green-800",
      "Written Off": "bg-gray-100 text-gray-800",
      "Claim Filed": "bg-purple-100 text-purple-800",
      Closed: "bg-gray-100 text-gray-800",
    };
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-semibold ${colors[status] || "bg-gray-100 text-gray-800"
          }`}
      >
        {status}
      </span>
    );
  };

  const getReportTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      Theft: "bg-red-100 text-red-800",
      Loss: "bg-orange-100 text-orange-800",
      Missing: "bg-yellow-100 text-yellow-800",
      Damaged: "bg-blue-100 text-blue-800",
    };
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-semibold ${colors[type] || "bg-gray-100 text-gray-800"
          }`}
      >
        {type}
      </span>
    );
  };

  const gridTheme = themeQuartz.withParams({
    backgroundColor: "#fff",
    foregroundColor: "#2a2a2a",
    headerTextColor: "#2a2a2a",
    headerBackgroundColor: "#E9F5FF",
    oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
    headerColumnResizeHandleColor: "rgb(126, 46, 132)",
    fontSize: "0.75rem",
    headerFontWeight: "bold",
  });

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        field: "reportNumber",
        headerName: "Report #",
        sortable: true,
        minWidth: 120,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
          fontWeight: "500",
        }),
      },
      {
        field: "assetName",
        headerName: "Equipment",
        sortable: true,
        minWidth: 180,
        flex: 1.5,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
      },
      {
        field: "reportType",
        headerName: "Type",
        sortable: true,
        minWidth: 120,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
        cellRenderer: (params: any) => {
          return getReportTypeBadge(params.value || "Theft");
        },
      },
      {
        field: "location",
        headerName: "Location",
        sortable: true,
        minWidth: 150,
        flex: 1.2,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
      },
      {
        field: "reportedAt",
        headerName: "Reported Date",
        sortable: true,
        minWidth: 150,
        flex: 1.2,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
        valueGetter: (params) => {
          if (!params.data?.reportedAt) return "";
          return new Date(params.data.reportedAt).toLocaleDateString();
        },
      },
      {
        field: "status",
        headerName: "Status",
        sortable: true,
        minWidth: 150,
        flex: 1.2,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
        cellRenderer: (params: any) => {
          return getStatusBadge(params.value || "Reported");
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        minWidth: 150,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "center",
        }),
        cellRenderer: (params: any) => {
          const report = params.data;
          return (
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => handleEdit(report)}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Edit"
              >
                <Edit className="w-5 h-5" />
              </button>
              {report.status !== "Recovered" &&
                report.status !== "Closed" && (
                  <button
                    onClick={() => handleRecover(report)}
                    className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    title="Mark as Recovered"
                  >
                    <CheckCircle className="w-5 h-5" />
                  </button>
                )}
            </div>
          );
        },
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
    }),
    []
  );

  const theme = useMemo(() => gridTheme, []);

  return (
    <div className="px-2 py-4 w-full h-full mt-10">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-3">
            <div className="bg-[#EAFBFA] p-3 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-[#0fa9a6]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Theft/Loss Reporting
              </h1>
              <p className="text-gray-600">
                Report and track stolen, lost, or missing equipment (from
                Equipment Management)
              </p>
            </div>
          </div>
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 px-4 py-2 bg-[#0fa9a6] text-white rounded-full hover:bg-teal-600 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Report Theft/Loss
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <input
                type="text"
                placeholder="Search reports..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#375998]"
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            </div>
          </div>
          <div className="min-w-[150px]">
            <Dropdown
              selectedValue={selectedRegion}
              onChange={setSelectedRegion}
              type="region"
            />
          </div>
          <div className="min-w-[150px]">
            <Dropdown
              selectedValue={selectedPremises}
              onChange={setSelectedPremises}
              type="premises"
              selectedRegion={selectedRegion}
            />
          </div>
          <div className="min-w-[150px]">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998]"
            >
              <option value="ALL">All Status</option>
              <option value="Reported">Reported</option>
              <option value="Under Investigation">Under Investigation</option>
              <option value="Recovered">Recovered</option>
              <option value="Written Off">Written Off</option>
              <option value="Claim Filed">Claim Filed</option>
              <option value="Closed">Closed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Reports List */}
      <div className="bg-white rounded-lg shadow-sm">
        {loading ? (
          <div className="flex justify-center items-center p-8">
            <SpinLoader />
          </div>
        ) : reports.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 text-gray-500">
            <FileText className="w-16 h-16 mb-4 text-gray-300" />
            <p className="text-lg font-medium mb-2">
              No theft/loss reports found
            </p>
            <p className="text-sm">
              {searchQuery || selectedRegion !== "ALL Regions"
                ? "Try adjusting your search or filters"
                : "Create your first theft/loss report"}
            </p>
          </div>
        ) : (
          <div className="tableBlock w-full">
            <div className="card">
              <div className="ag-theme-alpine w-full min-h-[400px] border-0 rounded-lg shadow-lg">
                <AgGridReact
                  rowData={reports}
                  columnDefs={columnDefs}
                  theme={theme}
                  headerHeight={65}
                  rowHeight={50}
                  loading={loading}
                  defaultColDef={defaultColDef}
                  domLayout="autoHeight"
                  pagination={true}
                  paginationPageSize={20}
                  paginationPageSizeSelector={[10, 20, 50]}
                  paginationAutoPageSize={false}
                  getRowStyle={() => ({
                    backgroundColor: "white",
                  })}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isCreateModalOpen || isEditModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setIsEditModalOpen(false);
          setSelectedReport(null);
        }}
        closeButton={false}
        modalClass="bg-white w-full max-w-3xl rounded-xl shadow-lg relative flex flex-col max-h-[80vh]"
      >
        <div className="pt-6 overflow-y-auto flex-1">
          <h2 className="text-xl font-semibold text-gray-800 border-b px-6 pb-2 mb-4">
            {isEditModalOpen
              ? "Update Theft/Loss Report Details"
              : "Create New Theft/Loss Report"}
          </h2>
          <form
            onSubmit={handleSubmit}
            id="theftLossForm"
            className="px-6 pb-6"
          >
            <div className="flex flex-col gap-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-gray-700">
                    Report Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.reportType}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        reportType: e.target.value as any,
                      })
                    }
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998]"
                    required
                  >
                    <option value="Theft">Theft</option>
                    <option value="Loss">Loss</option>
                    <option value="Missing">Missing</option>
                    <option value="Damaged">Damaged</option>
                  </select>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-gray-700">
                    Equipment <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.assetId}
                    onChange={(e) => {
                      const equipment = assets.find(
                        (a) => a.value === e.target.value
                      );
                      setFormData({
                        ...formData,
                        assetId: e.target.value,
                        assetName: equipment?.label || "",
                      });
                    }}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998]"
                    required
                  >
                    <option value="">Select equipment</option>
                    {assets.map((asset) => (
                      <option key={asset.value} value={asset.value}>
                        {asset.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">
                  Asset Tag (RFID/Barcode)
                </label>
                <input
                  type="text"
                  value={formData.assetTag}
                  onChange={(e) =>
                    setFormData({ ...formData, assetTag: e.target.value })
                  }
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998]"
                  placeholder="Enter asset tag if available"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-gray-700">
                    Location <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) =>
                      setFormData({ ...formData, location: e.target.value })
                    }
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998]"
                    required
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-gray-700">
                    Zone
                  </label>
                  <select
                    value={formData.zone}
                    onChange={(e) =>
                      setFormData({ ...formData, zone: e.target.value })
                    }
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998]"
                  >
                    <option value="">Select zone</option>
                    {zones.map((zone) => (
                      <option key={zone.value} value={zone.value}>
                        {zone.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows={4}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998]"
                  required
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">
                  Estimated Value
                </label>
                <input
                  type="number"
                  value={formData.estimatedValue || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      estimatedValue: parseFloat(e.target.value) || undefined,
                    })
                  }
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998]"
                  placeholder="Enter estimated value"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-gray-700">
                    Police Report Number
                  </label>
                  <input
                    type="text"
                    value={formData.policeReportNumber}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        policeReportNumber: e.target.value,
                      })
                    }
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998]"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-gray-700">
                    Police Report Date
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.policeReportDate}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        policeReportDate: e.target.value,
                      })
                    }
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-gray-700">
                    Insurance Claim Number
                  </label>
                  <input
                    type="text"
                    value={formData.insuranceClaimNumber}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        insuranceClaimNumber: e.target.value,
                      })
                    }
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998]"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-gray-700">
                    Insurance Claim Date
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.insuranceClaimDate}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        insuranceClaimDate: e.target.value,
                      })
                    }
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998]"
                  />
                </div>
              </div>
            </div>
          </form>
        </div>
        <div className="h-12" />
        <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
            onClick={() => {
              setIsCreateModalOpen(false);
              setIsEditModalOpen(false);
              setSelectedReport(null);
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="theftLossForm"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
          >
            {isEditModalOpen ? "Update" : "Submit Report"}
          </button>
        </div>
      </Modal>

      {/* Recovery Modal */}
      <Modal
        isOpen={isRecoverModalOpen}
        onClose={() => {
          setIsRecoverModalOpen(false);
          setSelectedReport(null);
        }}
        closeButton={false}
        modalClass="bg-white w-full max-w-md rounded-xl shadow-lg relative flex flex-col max-h-[80vh]"
      >
        <div className="pt-6 overflow-y-auto flex-1">
          <h2 className="text-xl font-semibold text-gray-800 border-b px-6 pb-2 mb-4">
            Mark Equipment as Recovered
          </h2>
          <form
            onSubmit={handleRecoverSubmit}
            id="recoveryForm"
            className="px-6 pb-6"
          >
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">
                  Recovery Location <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={recoveryData.recoveryLocation}
                  onChange={(e) =>
                    setRecoveryData({
                      ...recoveryData,
                      recoveryLocation: e.target.value,
                    })
                  }
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998]"
                  required
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">
                  Recovery Notes
                </label>
                <textarea
                  value={recoveryData.recoveryNotes}
                  onChange={(e) =>
                    setRecoveryData({
                      ...recoveryData,
                      recoveryNotes: e.target.value,
                    })
                  }
                  rows={4}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998] resize-y"
                />
              </div>
            </div>
          </form>
        </div>
        <div className="h-12" />
        <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
            onClick={() => {
              setIsRecoverModalOpen(false);
              setSelectedReport(null);
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="recoveryForm"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
          >
            Mark as Recovered
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default TheftLossReporting;
